import React, { useState, useEffect } from 'react';
import { 
    ArrowLeft, User, FileText, Save, 
    Loader2, Building, IndianRupee, X 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GET, POST } from '../services/api'; 

const CreateBusinessExchange = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // --- State ---
    const [cities, setCities] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const [formData, setFormData] = useState({
        city_id: '',
        chapter_id: '',
        member_id: '',      
        member_name: '',    
        member_image: '',   
        amount: '',
        remarks: ''
    });

    const [errors, setErrors] = useState({});

    // --- API Calls ---

    useEffect(() => {
        const fetchCities = async () => {
            try {
                const result = await GET('/app/cities');
                if (result && result.success) {
                    setCities(result.data);
                }
            } catch (error) {
                console.error(error);
            }
        };
        fetchCities();
    }, []);

    useEffect(() => {
        const fetchChapters = async () => {
            if (!formData.city_id) {
                setChapters([]);
                return;
            }
            try {
                const result = await GET(`/app/chapters/${formData.city_id}`);
                if (result && result.success) {
                    setChapters(result.data);
                } else {
                    setChapters([]);
                }
            } catch (error) {
                console.error(error);
                setChapters([]);
            }
        };
        fetchChapters();
    }, [formData.city_id]);

    useEffect(() => {
        if (formData.member_id) return;

        const timer = setTimeout(async () => {
            if (formData.member_name.length > 2) {
                setIsSearching(true);
                try {
                    const result = await GET(`/app/search-businesses?search=${formData.member_name}`);
                    if (result && result.success) {
                        setSearchResults(result.data);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    setIsSearching(false);
                }
            } else {
                setSearchResults([]);
            }
        }, 500); 

        return () => clearTimeout(timer);
    }, [formData.member_name, formData.member_id]);

    // --- Handlers ---

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'amount' && value < 0) return;

        if (name === 'city_id') {
            setFormData(prev => ({ ...prev, city_id: value, chapter_id: '' }));
        } else if (name === 'member_name') {
            setFormData(prev => ({ ...prev, member_name: value, member_id: '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSelectMember = (member) => {
        setFormData(prev => ({
            ...prev,
            member_id: member.user_id,
            member_name: member.full_name,
            member_image: member.profile_picture 
        }));
        setSearchResults([]); 
        if (errors.member_name) setErrors(prev => ({ ...prev, member_name: '' }));
    };

    const clearSelectedMember = () => {
        setFormData(prev => ({
            ...prev,
            member_id: '',
            member_name: '',
            member_image: ''
        }));
    };

    const validateForm = () => {
        let tempErrors = {};
        if (!formData.city_id) tempErrors.city_id = "City is required";
        if (!formData.chapter_id) tempErrors.chapter_id = "Chapter is required";
        if (!formData.member_id) tempErrors.member_name = "Please select a member";
        if (!formData.amount) tempErrors.amount = "Amount is required";
        
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);

        const payload = {
            to_member_id: formData.member_id,
            remarks: formData.remarks || "",
            amount: Number(formData.amount),
        };

        try {
            const result = await POST('/app/create-business-exchange', payload);

            if (result && result.success) {
                alert("Business Exchange Added Successfully!");
                navigate('/member/dashboard'); 
            } else {
                alert(result?.message || "Failed to add business exchange");
            }
        } catch (error) {
            alert(error.message || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] font-sans pb-28 pt-6 relative">
            
            <div className="container mx-auto px-4 max-w-lg">
                <div className="flex items-center gap-4 mb-8">
                    <button 
                        onClick={() => navigate(-1)} 
                        className="p-2.5 bg-white rounded-full shadow-sm text-gray-700 hover:text-[#1A87CF] transition-colors"
                    >
                        <ArrowLeft size={22} />
                    </button>
                    <h1 className="text-xl font-bold text-gray-900">Add Business Slip</h1>
                </div>

                <div className="bg-white rounded-[30px] p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 relative">
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                        
                        {/* City & Chapter */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                    City <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <select
                                        name="city_id"
                                        value={formData.city_id}
                                        onChange={handleChange}
                                        className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 appearance-none text-sm font-medium outline-none transition-all
                                            ${errors.city_id ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                        `}
                                    >
                                        <option value="">Select City</option>
                                        {cities.map(city => (
                                            <option key={city._id} value={city._id}>{city.name}</option>
                                        ))}
                                    </select>
                                    <Building size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                                {errors.city_id && <p className="text-xs text-red-500 mt-1 ml-1">{errors.city_id}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                    Chapter <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <select
                                        name="chapter_id"
                                        value={formData.chapter_id}
                                        onChange={handleChange}
                                        disabled={!formData.city_id} 
                                        className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 appearance-none text-sm font-medium outline-none transition-all
                                            ${errors.chapter_id ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                            ${!formData.city_id ? 'opacity-50 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        <option value="">Select Chapter</option>
                                        {chapters.map(chap => (
                                            <option key={chap._id} value={chap._id}>{chap.name}</option>
                                        ))}
                                    </select>
                                    <Building size={18} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                </div>
                                {errors.chapter_id && <p className="text-xs text-red-500 mt-1 ml-1">{errors.chapter_id}</p>}
                            </div>
                        </div>

                        {/* Member Search */}
                        <div className="relative">
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                Given To <span className="text-red-500">*</span>
                            </label>
                            
                            <div className="relative">
                                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                    {formData.member_image ? (
                                        <img 
                                            src={formData.member_image} 
                                            alt="User" 
                                            className="w-6 h-6 rounded-full object-cover border border-gray-200"
                                        />
                                    ) : (
                                        <User size={20} />
                                    )}
                                </div>

                                <input 
                                    type="text" 
                                    name="member_name"
                                    value={formData.member_name}
                                    onChange={handleChange}
                                    readOnly={!!formData.member_id}
                                    placeholder="Search member name..."
                                    className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 text-sm font-medium outline-none transition-all pl-12 pr-10
                                        ${errors.member_name ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                    `}
                                />

                                {formData.member_id && (
                                    <button 
                                        type="button"
                                        onClick={clearSelectedMember}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full bg-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-500"
                                    >
                                        <X size={16} />
                                    </button>
                                )}

                                {isSearching && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <Loader2 size={18} className="animate-spin text-[#1A87CF]" />
                                    </div>
                                )}
                            </div>

                            {/* Results Dropdown */}
                            {searchResults.length > 0 && !formData.member_id && (
                                <div className="absolute z-10 w-full mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 max-h-60 overflow-y-auto">
                                    {searchResults.map((user) => (
                                        <div 
                                            key={user._id}
                                            onClick={() => handleSelectMember(user)}
                                            className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors"
                                        >
                                            <img 
                                                src={user.profile_picture || "https://cdn-icons-png.flaticon.com/512/847/847969.png"} 
                                                alt="profile" 
                                                className="w-10 h-10 rounded-full object-cover border border-gray-200 bg-gray-100"
                                            />
                                            <div>
                                                <p className="text-sm font-bold text-gray-800">{user.full_name}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {errors.member_name && <p className="text-xs text-red-500 mt-1 ml-1">{errors.member_name}</p>}
                        </div>

                        {/* Amount */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                Amount (INR) <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    placeholder="Enter amount (e.g., 5000)"
                                    className={`w-full p-3.5 bg-gray-50 rounded-2xl border-2 text-sm font-medium outline-none transition-all pl-11
                                        ${errors.amount ? 'border-red-300' : 'border-transparent focus:border-[#1A87CF]/30 focus:bg-white'}
                                    `}
                                />
                                <IndianRupee size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"/>
                            </div>
                            {errors.amount && <p className="text-xs text-red-500 mt-1 ml-1">{errors.amount}</p>}
                        </div>

                        {/* Remarks */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 ml-1">
                                Remarks
                            </label>
                            <div className="relative">
                                <textarea 
                                    name="remarks"
                                    value={formData.remarks}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Enter details about the business..."
                                    className="w-full p-3.5 bg-gray-50 rounded-2xl border-2 border-transparent focus:border-[#1A87CF]/30 focus:bg-white text-sm font-medium outline-none transition-all resize-none pl-11"
                                ></textarea>
                                <FileText size={20} className="absolute left-3.5 top-4 text-gray-400"/>
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            disabled={loading}
                            className="w-full py-4 bg-[#1A87CF] text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-200/50 hover:shadow-xl hover:shadow-blue-200/70 hover:-translate-y-0.5 transition-all flex justify-center items-center gap-2 mt-4"
                        >
                            {loading ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                            {loading ? 'Submitting...' : 'Save Business Slip'}
                        </button>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateBusinessExchange;